// Borrowed from https://bit.ly/2VupzKY

import { useEffect, useRef, useState } from 'react';

import { ensureArray } from '@zapier/common-utils';

type Options = {
  once?: boolean;
  root?: HTMLElement;
  rootMargin?: string;
  threshold?: number;
};

type Metadata = {
  isTopIntersecting?: boolean;
  isBottomIntersecting?: boolean;
  isLeftIntersecting?: boolean;
  isRightIntersecting?: boolean;
};

type EntryData = {
  entry: IntersectionObserverEntry | null;
};

export type UseIntersectReturn = [
  (element: Element | null) => void,
  Metadata & EntryData
];

const useIntersect = ({
  once = false,
  root,
  rootMargin = '0px',
  threshold = 1,
}: Options = {}): UseIntersectReturn => {
  // Hold a reference to the `IntersectionObserver` entry.
  const [entry, updateEntry] = useState<IntersectionObserverEntry | null>(null);
  // Hold a reference for our own metadata about the entry.
  const [meta, updateMeta] = useState<Metadata | null>(null);
  // Hold a reference to the DOM node being observed.
  const [node, setNode] = useState<Element | null>(null);
  // Hold a reference to the `IntersectionObserver` itself.
  const observer = useRef<IntersectionObserver>(null);
  useEffect(() => {
    const disconnect = () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
    // Disconnect since something changed and therefore
    // the node needs to be reobserved.
    disconnect();
    // calculates the intersection metadata for an entry
    const getMetadata = ({
      intersectionRatio,
      intersectionRect,
      rootBounds,
    }: IntersectionObserverEntry) => {
      const isIntersecting =
        !!intersectionRect && !!rootBounds && intersectionRatio < threshold;
      return {
        isTopIntersecting:
          isIntersecting && intersectionRect.top <= rootBounds!.top,
        isBottomIntersecting:
          isIntersecting && intersectionRect.bottom >= rootBounds!.bottom,
        isLeftIntersecting:
          isIntersecting && intersectionRect.left <= rootBounds!.left,
        isRightIntersecting:
          isIntersecting && intersectionRect.right >= rootBounds!.right,
      };
    };
    // Update the entry and metadata when the observation changes.
    const callback: IntersectionObserverCallback = ([updatedEntry]) => {
      updateEntry(updatedEntry);
      updateMeta(getMetadata(updatedEntry));
      if (once) {
        disconnect();
      }
    };
    // Create the observer.
    const options = { root, rootMargin, threshold: ensureArray(threshold) };
    if (window.IntersectionObserver) {
      // @ts-ignore
      observer.current = new window.IntersectionObserver(callback, options);
    }
    // Actually start observing `node`.
    if (node && observer.current) {
      observer.current.observe(node);
    }
    return disconnect;
  }, [once, node, root, rootMargin, threshold]);
  return [setNode, { entry, ...meta }];
};

export default useIntersect;
